import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch } from '@fortawesome/free-solid-svg-icons';
import debounce from 'lodash/debounce';
import i18n from '../i18n';
import api from '../api';
import '../css/search.css';

const buildNavigation = (item) => {
    switch (item.type) {
        case 'user':
            return `/user/${item.id}`;
        case 'post':
            return `/posts/${item.id}`;
        case 'news':
            return `/news/${item.id}`;
        case 'patch_note':
            return `/patch-notes/${item.id}`;
        case 'character':
            return `/characters?id=${item.id}`;
        default:
            return '/';
    }
};

const SearchBar = ({ autoFocus = false, onClose, collapsible = false }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [query, setQuery] = useState('');
    const [results, setResults] = useState(null);
    const [loading, setLoading] = useState(false);
    const [isOpen, setIsOpen] = useState(false);
    const [expanded, setExpanded] = useState(!collapsible);
    const containerRef = useRef(null);
    const inputRef = useRef(null);
    const queryRef = useRef('');

    const fetchResults = useCallback(async (term) => {
        const trimmed = term.trim();
        if (trimmed.length < 2) {
            setResults(null);
            setLoading(false);
            return;
        }

        setLoading(true);
        const lang = i18n.language.includes('-') ? i18n.language.split('-')[0] : i18n.language;

        try {
            const response = await api.get(`${process.env.REACT_APP_API_URL}/api/search`, {
                params: { q: trimmed, lang, limit: 5 },
            });
            setResults(response.data);
            setIsOpen(true);
        } catch (error) {
            setResults(null);
        } finally {
            setLoading(false);
        }
    }, []);

    const debouncedFetch = useCallback(debounce((term) => fetchResults(term), 300), [fetchResults]);

    useEffect(() => {
        return () => debouncedFetch.cancel();
    }, [debouncedFetch]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (containerRef.current && !containerRef.current.contains(event.target)) {
                setIsOpen(false);
                if (!queryRef.current.trim()) {
                    if (collapsible && expanded) {
                        setExpanded(false);
                    }
                    if (onClose) {
                        onClose();
                    }
                }
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [onClose, collapsible, expanded]);

    const handleChange = (e) => {
        const value = e.target.value;
        setQuery(value);
        queryRef.current = value;
        debouncedFetch(value);
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Escape') {
            setQuery('');
            queryRef.current = '';
            setResults(null);
            setIsOpen(false);
            if (collapsible) {
                setExpanded(false);
                inputRef.current?.blur();
            }
            if (onClose) {
                onClose();
            }
        }
    };

    const handleExpand = () => {
        if (collapsible && !expanded) {
            setExpanded(true);
            setTimeout(() => inputRef.current?.focus(), 0);
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        const trimmed = query.trim();
        if (trimmed.length >= 2) {
            setIsOpen(false);
            navigate(`/search?q=${encodeURIComponent(trimmed)}`);
        }
    };

    const handleItemClick = (item) => {
        setIsOpen(false);
        setQuery('');
        setResults(null);
        navigate(buildNavigation(item));
    };

    const totalCount = results
        ? results.users.length + results.posts.length + results.news.length +
          results.patch_notes.length + results.characters.length
        : 0;

    return (
        <div
            className={`search-bar${collapsible ? ' search-bar--collapsible' : ''}${collapsible && !expanded ? ' search-bar--collapsed' : ''}`}
            ref={containerRef}
        >
            <form onSubmit={handleSubmit} className="search-bar__form" onClick={handleExpand}>
                <FontAwesomeIcon icon={faSearch} className="search-bar__icon" />
                <input
                    ref={inputRef}
                    type="text"
                    className="search-bar__input"
                    placeholder={t('search.placeholder')}
                    value={query}
                    onChange={handleChange}
                    onKeyDown={handleKeyDown}
                    autoFocus={autoFocus}
                    onFocus={() => results && totalCount > 0 && setIsOpen(true)}
                />
            </form>

            {isOpen && (
                <div className="search-bar__dropdown">
                    {loading && (
                        <div className="search-bar__empty">{t('search.loading')}</div>
                    )}

                    {!loading && results && totalCount === 0 && (
                        <div className="search-bar__empty">{t('search.noResults')}</div>
                    )}

                    {!loading && results && totalCount > 0 && (
                        <div className="search-bar__sections">
                            {results.users.length > 0 && (
                                <div className="search-bar__section">
                                    <div className="search-bar__section-title">{t('search.users')}</div>
                                    {results.users.map((u) => (
                                        <button key={`user-${u.id}`} className="search-bar__item" onClick={() => handleItemClick(u)}>
                                            <span className="search-bar__item-main">{u.username}</span>
                                            <span className="search-bar__item-meta">{t('search.user')} · {t('search.level')} {u.level}</span>
                                        </button>
                                    ))}
                                </div>
                            )}
                            {results.posts.length > 0 && (
                                <div className="search-bar__section">
                                    <div className="search-bar__section-title">{t('search.posts')}</div>
                                    {results.posts.map((p) => (
                                        <button key={`post-${p.id}`} className="search-bar__item" onClick={() => handleItemClick(p)}>
                                            <span className="search-bar__item-main">{p.content}</span>
                                            <span className="search-bar__item-meta">{t('search.post')} · {p.username}</span>
                                        </button>
                                    ))}
                                </div>
                            )}
                            {results.news.length > 0 && (
                                <div className="search-bar__section">
                                    <div className="search-bar__section-title">{t('search.news')}</div>
                                    {results.news.map((n) => (
                                        <button key={`news-${n.id}`} className="search-bar__item" onClick={() => handleItemClick(n)}>
                                            <span className="search-bar__item-main">{n.title}</span>
                                            <span className="search-bar__item-meta">{t('search.news')}</span>
                                        </button>
                                    ))}
                                </div>
                            )}
                            {results.patch_notes.length > 0 && (
                                <div className="search-bar__section">
                                    <div className="search-bar__section-title">{t('search.patchNotes')}</div>
                                    {results.patch_notes.map((p) => (
                                        <button key={`patch-${p.id}`} className="search-bar__item" onClick={() => handleItemClick(p)}>
                                            <span className="search-bar__item-main">{p.title}</span>
                                            <span className="search-bar__item-meta">{t('search.patchNote')}{p.version ? ` · v${p.version}` : ''}</span>
                                        </button>
                                    ))}
                                </div>
                            )}
                            {results.characters.length > 0 && (
                                <div className="search-bar__section">
                                    <div className="search-bar__section-title">{t('search.characters')}</div>
                                    {results.characters.map((c) => (
                                        <button key={`char-${c.id}`} className="search-bar__item" onClick={() => handleItemClick(c)}>
                                            <span className="search-bar__item-main">{c.name}</span>
                                            <span className="search-bar__item-meta">{t('search.character')}</span>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default SearchBar;
